import React, { useState, useEffect, useMemo } from 'react';
import { Spin, Empty } from 'antd';
import { useSession } from 'next-auth/react';
import { useFindManyUserUploadedAssetsQuery } from '@src/components/generated/graphql';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import styles from './npc-grid-selector.module.css';

const MESSAGES = {
  LOADING: 'Loading NPCs...',
  NO_NPCS: 'No NPCs uploaded yet. Upload a model first.',
  SELECT_PLACEHOLDER: 'Select NPC from inventory',
} as const;

interface NPCAsset {
  id: string;
  name: string;
  assetUrl: string;
  assetType: string;
  previewImage?: string;
}

interface NPCGridSelectorProps {
  onNPCSelected: (
    inventoryItemId: string,
    url: string,
    previewUrl: string,
    name: string
  ) => Promise<void>;
  currentNPCId?: string;
  disabled?: boolean;
  npcPreviews?: Record<string, string>;
}

const isNPCAsset = (asset: any): asset is NPCAsset => {
  return asset?.assetType === ASSET_TYPE.GLTF.OTHER;
};

const NPCGridSelector: React.FC<NPCGridSelectorProps> = ({
  onNPCSelected,
  currentNPCId,
  disabled = false,
  npcPreviews = {},
}) => {
  const { data: session } = useSession();
  const [selectedNPCId, setSelectedNPCId] = useState<string>('');

  const { data, loading, error } = useFindManyUserUploadedAssetsQuery({
    variables: {
      where: {
        userId: {
          equals: session?.user.id!,
        },
      },
    },
    fetchPolicy: 'no-cache',
    skip: !session?.user?.id,
  });

  const npcAssets = useMemo(() => {
    if (!data?.findManyUserUploadedAssets) return [];
    return data.findManyUserUploadedAssets.filter(isNPCAsset).map((asset) => ({
      ...asset,
      previewImage:
        asset.previewImage || npcPreviews[asset.assetUrl] || undefined,
    }));
  }, [data?.findManyUserUploadedAssets, npcPreviews]);

  useEffect(() => {
    if (currentNPCId) {
      const currentNPC = npcAssets.find((npc) => npc.id === currentNPCId);
      if (currentNPC) {
        setSelectedNPCId(currentNPC.id);
      }
    }
  }, [currentNPCId, npcAssets]);

  const handleNPCClick = async (npc: NPCAsset): Promise<void> => {
    if (disabled) return;

    setSelectedNPCId(npc.id);
    await onNPCSelected(npc.id, npc.assetUrl, npc.previewImage || '', npc.name);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size='small' />
        <span>{MESSAGES.LOADING}</span>
      </div>
    );
  }

  if (error) {
    console.error('Error loading NPCs:', error);
    return (
      <div className={styles.emptyContainer}>
        <span>Error loading NPCs. Please try again.</span>
      </div>
    );
  }

  if (npcAssets.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <Empty
          description={MESSAGES.NO_NPCS}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className={styles.npcGridSelector}>
      <div className={styles.npcGrid}>
        {npcAssets.map((npc) => (
          <div
            key={npc.id}
            className={`${styles.npcCard} ${
              selectedNPCId === npc.id ? styles.selected : ''
            } ${disabled ? styles.disabled : ''}`}
            onClick={() => handleNPCClick(npc)}
          >
            <div className={styles.npcImage}>
              {npc.previewImage && npc.previewImage.startsWith('http') ? (
                <img
                  src={npc.previewImage}
                  alt={npc.name}
                  className={styles.previewImage}
                />
              ) : (
                <div className={styles.placeholderImage}>
                  <div className={styles.placeholderIcon}>🤖</div>
                </div>
              )}
            </div>
            <div className={styles.npcInfo}>
              <div className={styles.npcName} title={npc.name}>
                {npc.name}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NPCGridSelector;
